import * as React from 'react';
import s from './TruncatedText.scss';
import classNames from 'classnames';
import { Text } from '@wix/design-system';

export interface TruncatedTextProps {
  text: string;
  dataHook?: string;
  maxWidth?: number; // width in pixels
  svgText?: boolean;
  as?: React.JSXElementConstructor<any> | string;
  componentProps?: any;
}

export const TruncatedText = (props: TruncatedTextProps) => {
  const delimiter = '...';
  const rootSpanRef = React.useRef<HTMLSpanElement>(null);
  const testNodeSpanRef = React.useRef<HTMLSpanElement>(null);

  const { text, maxWidth, componentProps = {}, dataHook = '' } = props;
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [displayTruncatedText, setDisplayTruncatedText] = React.useState(text);
  const Component = props.as || Text;

  React.useEffect(() => {
    setIsInitialized(true);
    setDisplayTruncatedText(getTruncatedText);
  }, []);

  React.useEffect(() => {
    updateTruncatedText();
  }, [props.maxWidth, props.text]);

  const updateTruncatedText = () => {
    setDisplayTruncatedText(getTruncatedText());
  };

  const getTruncatedText = () => {
    const requiredWidth = getTestNodeWidthWithContent(text);
    const rootRef = rootSpanRef;
    let currentMaxWidth = maxWidth || 0;
    let currentText = text;

    if (!maxWidth && rootRef.current) {
      currentMaxWidth = rootRef.current.getBoundingClientRect().width;
    }

    if (requiredWidth > currentMaxWidth) {
      const halfWidth = getHalfWidth(currentMaxWidth);

      const leftPart = getHalfDisplayPart({
        width: halfWidth,
        value: text.slice(0, text.length / 2),
      });

      const rightPart = getHalfDisplayPart({
        width: halfWidth,
        value: text.slice(text.length / 2),
        reverse: true,
      });

      currentText = leftPart + delimiter + rightPart;
    }

    return currentText;
  };

  const getHalfDisplayPart = ({
    width,
    value,
    reverse = false,
  }: {
    width: number;
    value: string;
    reverse?: boolean;
  }) => {
    let currentDisplayPart = value;
    let currentDisplayPartLength;

    do {
      currentDisplayPart = reverse
        ? currentDisplayPart.slice(1)
        : currentDisplayPart.slice(0, currentDisplayPart.length - 1);

      currentDisplayPartLength = getTestNodeWidthWithContent(
        currentDisplayPart,
      );
    } while (currentDisplayPartLength > width);

    return currentDisplayPart;
  };

  const getTestNodeWidthWithContent = (content: string) => {
    const testNode = testNodeSpanRef.current;
    if (testNode) {
      testNode.innerHTML = content;
      return testNode.getBoundingClientRect().width;
    }
    return 0;
  };

  const getHalfWidth = (width: number) => {
    const delimiterLength = getTestNodeWidthWithContent(delimiter);

    return (width - delimiterLength) / 2;
  };

  const rootClassName = classNames({
    [s.root]: true,
    [s.initialized]: isInitialized,
  });

  return (
    <span className={rootClassName} ref={rootSpanRef}>
      <Component {...componentProps} dataHook='truncated-text'>
        {displayTruncatedText}
      </Component>
      <span className={s.testNodeWrapper}>
        <Component {...componentProps}>
          <span
            ref={testNodeSpanRef}
            data-hook={dataHook}
            className={s.testText}
          />
        </Component>
      </span>
    </span>
  );
};
